import { wordsMatch } from '../spell-check-state'
import type { SpellIssue } from '../types/spell-issue'
import type { SpellOccurrence } from '../types/spell-occurrence'

export interface FoundOccurrence {
  issueId: string
  index: number
}

/**
 * Finds again an instance the user was looking at, in a state read later. Issue ids and indexes
 * move whenever the text is edited or Case Sensitive flips, so the instance is found by where it
 * sits (its shape and start offset), and only while the word there is still the same word: a typo
 * edited into a different one is not found, so a stale action cannot change unrelated text. An
 * instance with no position, as in a hand-built state, is matched by its issue id and index.
 * Returns `null` when the instance is gone or no longer open.
 */
export function findOpenOccurrence(
  issues: readonly SpellIssue[],
  seen: SpellIssue,
  seenIndex: number,
  caseSensitive: boolean
): FoundOccurrence | null {
  const target = seen.occurrences[seenIndex]

  if (!target) return null

  const hasPosition = target.shapeId !== undefined && target.start !== undefined

  const isTarget = (occurrence: SpellOccurrence, index: number, issue: SpellIssue) =>
    occurrence.status === 'open' &&
    (hasPosition
      ? occurrence.shapeId === target.shapeId &&
        occurrence.start === target.start &&
        occurrence.onPath === target.onPath
      : issue.id === seen.id && index === seenIndex)

  for (const issue of issues) {
    if (!wordsMatch(issue.word, seen.word, caseSensitive)) continue

    const index = issue.occurrences.findIndex((occurrence, i) => isTarget(occurrence, i, issue))

    if (index !== -1) return { issueId: issue.id, index }
  }

  return null
}
